import { Image, Linking, Modal, Platform, StyleSheet, ScrollView, View, Pressable, Share, TextInput, useWindowDimensions } from "react-native";
import { FindifyTopBarOverlay } from "./components/FindifyTopBarOverlay";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { BasePage } from "./helpers/BasePage";
import { PlaceColors } from "./helpers/PlaceColors";
import { FindifyDataContext } from "@/contexts/FindifyDataContext";
import { FindifySimpleHeader, SimpleHeaderAction } from "./components/FindifySimpleHeader";
import { FindifyHTMLRender } from "./helpers/FindifyHTMLRender";
import { PlaceText } from "./components/PlaceText";
import { useTranslation } from "react-i18next";
import { BiscainhosFooterLogo, BottomNavigationPadding } from "./components/FindifyNavigation";
import { FindifyLoadingScreen } from "./components/FindifyLoadingScreen";
import { IconShadow } from "./helpers/DefaultShadows";

import { MakeContentDetailsContentVars, MakeContentDetailsSetActions, PlaceContentDetailsInitialUseEffect, getContentIdForModule, getFeaturedImageUrl, makeDateString, makeContentTitle } from "@/layouts/genericPages/helpers/DetailsHelpers";
import { ContentDetailsGallery } from "@/layouts/genericPages/components/ContentDetailsGallery";
import RNMapbox, { StyleURL } from "@rnmapbox/maps";
import Icon from "react-native-vector-icons/Ionicons";
import { getTranslatedString } from "./helpers/FindifyTranslatedString";
import { getApiFromModule } from "./helpers/GetContentsByModule";
import { BiscainhosAppHeader } from "./components/BiscainhosAppHeader";
import { useSeoTitle } from "./helpers/SeoTitle";
import { getContent3dUrl, getContentAudioUrl, getContentVideoUrl } from "./helpers/MediaHelpers";
import { SimpleAudioPlayer } from "./components/SimpleAudioPlayer";
import { getWorkspaceTitle } from "./helpers/WorkspaceText";

export const PlaceContentDetailsTab = ({ navigation, ...props }: any) => {
    const [contentModule, setContentModule] = useState<any>();
    const [contentSlug, setContentSlug] = useState<any>();
    const [hideDate, setHideDate] = useState(false);
    const { t } = useTranslation();
    const [content, setContent] = useState<any>();
    const findifyDataContext = useContext(FindifyDataContext);

    useEffect(() => {
        PlaceContentDetailsInitialUseEffect(props, findifyDataContext, setContent, setContentSlug, setContentModule, setHideDate);
    }, [])


    return <BasePage navigation={navigation}>
        <FindifyTopBarOverlay />
        {content && makeContentDetails(MakeContentDetailsPage, content, t, hideDate, contentModule, navigation, findifyDataContext)}
        {!content && <FindifyLoadingScreen />}
    </BasePage>;
};


export const makeContentDetails = (MakeContentDetailsPage: JSX.ElementType, content: any, t: any, hideDate: boolean, module: string, navigation: any, findifyDataContext: any, isLogged?: boolean) => {
    const {
        image,
        title,
        resume,
        description,
        dateString,
        rightActions,
        bottomButtons,
    } = MakeContentDetailsContentVars(content, module, findifyDataContext, t, navigation);

    return <MakeContentDetailsPage
        image={image}
        title={title}
        resume={resume}
        description={description}
        dateString={dateString}
        hideDate={hideDate}
        navigation={navigation}
        rightActions={rightActions}
        content={content}
        module={module}
        findifyDataContext={findifyDataContext}
        t={t}
        bottomButtons={bottomButtons}
        isLogged={isLogged}
    />
}



type ContentDetailsType = {
    image: string,
    title: string,
    resume: string,
    description: string,
    dateString: string,
    hideDate: boolean,
    navigation: any,
    rightActions: JSX.Element,
    bottomButtons: JSX.Element,
    content: any,
    findifyDataContext: any,
    module: string,
    t: any,
}
const MakeContentDetailsPage = ({ image, title, navigation, resume, description, dateString, rightActions, content, t, hideDate, module, findifyDataContext, bottomButtons }: ContentDetailsType) => {
    const topTextStyle = {
        marginTop: 5,
        fontSize: 14,
        color: PlaceColors(findifyDataContext).text.withAlpha(160)
    };
    const [actionsList, setActionsList] = useState<SimpleHeaderAction[]>([]);
    const [textSizeModifier, setTextSizeModifier] = useState<number>(0);
    useSeoTitle(title, getWorkspaceTitle(findifyDataContext));

    useEffect(() => {
        MakeContentDetailsSetActions(setActionsList, title, findifyDataContext, content, module);
    }, [module])

    if (content?.__loadError) {
        return <View style={[styles.errorContainer, { backgroundColor: PlaceColors(findifyDataContext).background.get() }]}>
            <PlaceText style={[styles.errorTitle, { color: PlaceColors(findifyDataContext).primary.get() }]}>
                {t('base.error') || 'Error'}
            </PlaceText>
            <PlaceText style={[styles.errorText, { color: PlaceColors(findifyDataContext).text.get() }]}>
                Não foi possível carregar este conteúdo.
            </PlaceText>
        </View>
    }

    const videoUrl = getContentVideoUrl(content, findifyDataContext?.language);
    const audioUrl = getContentAudioUrl(content, findifyDataContext?.language);

    if (module === 'contents' && findifyDataContext?.workspace?.workspace_id === 155 && isReviewContent(content)) {
        return <BiscainhosReviewDetails
            content={content}
            navigation={navigation}
            findifyDataContext={findifyDataContext}
            bottomButtons={bottomButtons}
        />
    }

    if (module === 'contents' && findifyDataContext?.workspace?.workspace_id === 155) {
        return <BiscainhosGenericContentDetails
            content={content}
            image={image}
            title={title}
            resume={resume}
            description={description}
            navigation={navigation}
            findifyDataContext={findifyDataContext}
            videoUrl={videoUrl}
            audioUrl={audioUrl}
            bottomButtons={bottomButtons}
            t={t}
        />
    }

    if (module === 'points_of_interest' && findifyDataContext?.workspace?.workspace_id === 155) {
        return <BiscainhosPoiDetails
            content={content}
            image={image}
            navigation={navigation}
            findifyDataContext={findifyDataContext}
            videoUrl={videoUrl}
            audioUrl={audioUrl}
            bottomButtons={bottomButtons}
            t={t}
        />
    }

    return <>
        <ScrollView
            bounces={false}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            style={[styles.container, { backgroundColor: PlaceColors(findifyDataContext).background.get() }]}>
            <FindifySimpleHeader
                navigation={navigation}
                title={title}
                actions={actionsList}
                image={image} />
            <View style={[{
                backgroundColor: PlaceColors(findifyDataContext).background.get(),
                borderTopLeftRadius: 24,
                borderTopRightRadius: 24,
                marginTop: -24,
                padding: 20,
                paddingBottom: 20 + BottomNavigationPadding,
            }]}>
                <View style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 10,
                }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        {!hideDate &&
                            <PlaceText style={{
                                fontSize: 14,
                                color: PlaceColors(findifyDataContext).text.withAlpha(200),
                                fontWeight: '500',
                            }}>{dateString}</PlaceText>
                        }
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: PlaceColors(findifyDataContext).primary.withAlpha(20), borderRadius: 20, paddingHorizontal: 8, paddingVertical: 4 }}>
                            <Pressable onPress={() => setTextSizeModifier(prev => Math.max(prev - 2, -4))} style={{ paddingHorizontal: 8 }}>
                                <PlaceText style={{ fontSize: 16, fontWeight: 'bold', color: PlaceColors(findifyDataContext).primary.get() }}>A-</PlaceText>
                            </Pressable>
                            <View style={{ width: 1, height: 16, backgroundColor: PlaceColors(findifyDataContext).primary.withAlpha(50) }} />
                            <Pressable onPress={() => setTextSizeModifier(prev => Math.min(prev + 2, 8))} style={{ paddingHorizontal: 8 }}>
                                <PlaceText style={{ fontSize: 18, fontWeight: 'bold', color: PlaceColors(findifyDataContext).primary.get() }}>A+</PlaceText>
                            </Pressable>
                        </View>
                        {rightActions}
                    </View>
                </View>
                
                {/* address / resume */}
                {!!resume && <PlaceText style={[topTextStyle, { fontSize: 15 + (textSizeModifier * 0.5), marginBottom: 15, fontWeight: '600', color: PlaceColors(findifyDataContext).text.get() }]}>{resume}</PlaceText>}

                {videoUrl && <ContentVideoPlayer videoUrl={videoUrl} />}

                <FindifyHTMLRender
                    baseStyles={{
                        color: PlaceColors(findifyDataContext).text.get(),
                        fontSize: 16 + textSizeModifier,
                        lineHeight: (16 + textSizeModifier) * 1.5,
                    }}
                    tagsStyles={{ p: { marginBottom: 10 } }}
                    classesStyles={{ PlaygroundEditorTheme__paragraph: { marginBottom: 10 } }}
                    htmlCode={description} />

                {module === 'routes' && <RouteDetailsMap content={content} findifyDataContext={findifyDataContext} navigation={navigation} />}

                {content.galleries && <View style={{ marginTop: 20 }}><ContentDetailsGallery findifyDataContext={findifyDataContext} t={t} content={content} navigation={navigation} /></View>}
                <BiscainhosFooterLogo findifyDataContext={findifyDataContext} />
            </View >
        </ScrollView>
        {bottomButtons}
    </>
}

const ContentVideoPlayer = ({ videoUrl }: { videoUrl: string }) => {
    if (Platform.OS === 'web') {
        return <View style={styles.videoContainer}>
            {React.createElement('video', {
                src: videoUrl,
                controls: true,
                playsInline: true,
                style: {
                    width: '100%',
                    height: '100%',
                    borderRadius: 14,
                    backgroundColor: '#000',
                    objectFit: 'contain',
                },
            })}
        </View>
    }

    return null;
}

const stripHtml = (html: string) => {
    return (html ?? '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+\n/g, '\n')
        .replace(/\n\s+/g, '\n')
        .trim();
}

const titleCase = (text: string) => {
    return (text ?? '').toLocaleLowerCase('pt-PT').replace(/(^|\s|\/)(\p{L})/gu, (match) => match.toLocaleUpperCase('pt-PT'));
}

const getPoiTitle = (content: any, language: string) => {
    return titleCase(content?.title?.[language] ?? content?.title?.['en-US'] ?? content?.title?.['pt-PT'] ?? '');
}

const getPoiDescription = (content: any, language: string) => {
    return stripHtml(content?.description?.[language] ?? '');
}

const isEnglishLanguage = (language?: string) => language?.startsWith('en');
const isFrenchLanguage = (language?: string) => language?.startsWith('fr');
const localizedText = (findifyDataContext: any, pt: string, en: string, fr?: string) => {
    if (isFrenchLanguage(findifyDataContext?.language)) return fr ?? en;
    return isEnglishLanguage(findifyDataContext?.language) ? en : pt;
};

const getTranslatedValues = (value: any) => {
    if (!value) return [];
    if (typeof value === 'string') return [value];
    if (typeof value === 'object') return Object.values(value).filter(Boolean).map((line: any) => String(line));
    return [];
}

const getRouteCategories = (route: any) => (route?.categories ?? [])
    .map((line: any) => line?.category ?? line)
    .filter((category: any) => category?.category_id);

const isAccessibleRouteCategory = (category: any) => {
    const label = [category?.page_slug, ...getTranslatedValues(category?.name)]
        .join(' ').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    return /acessivel|accessible|accessibilite/.test(label);
}

const belongsToSelectedVisit = (route: any, visitVersion: 'accessible' | 'standard' | null) => {
    const categories = getRouteCategories(route);
    if (categories.length === 0) return false;
    return visitVersion === 'accessible'
        ? categories.some(isAccessibleRouteCategory)
        : categories.some((category: any) => !isAccessibleRouteCategory(category));
}

const isPublishedRoute = (route: any) => route?.published !== false
    && route?.published !== 0
    && route?.published !== '0';

const compareRoutesByPublication = (a: any, b: any) => {
    const aPublishedAt = Date.parse(a?.make_public_on ?? '');
    const bPublishedAt = Date.parse(b?.make_public_on ?? '');
    const aOrder = Number.isFinite(aPublishedAt) ? aPublishedAt : Number.MAX_SAFE_INTEGER;
    const bOrder = Number.isFinite(bPublishedAt) ? bPublishedAt : Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder || Number(a?.position ?? a?.route_id ?? 0) - Number(b?.position ?? b?.route_id ?? 0);
}

const findRoomObjectsCategory = (categories: any[], roomSlug?: string) => {
    if (!roomSlug) return null;
    const expectedName = `#${roomSlug}`.toLocaleLowerCase();
    return categories.find((category: any) => {
        const names = getTranslatedValues(category?.name).map((line: string) => line.trim().toLocaleLowerCase());
        const pageSlug = String(category?.page_slug ?? '').trim().toLocaleLowerCase();
        return names.includes(expectedName)
            || pageSlug === expectedName
            || pageSlug === roomSlug.toLocaleLowerCase();
    }) ?? null;
}

const isRoomObject = (point: any, roomCategoryId: number, roomSlug: string) => {
    const expectedCategoryName = `#${roomSlug}`.trim().toLocaleLowerCase();

    return (point?.categories ?? []).some((line: any) => {
        const category = line?.category ?? line;
        const categoryId = Number(category?.category_id ?? line?.category_id);
        const categoryNames = getTranslatedValues(category?.name)
            .map((name: string) => name.trim().toLocaleLowerCase());

        // A category name is the source of truth for the room association.
        // The id check remains as a fallback for public responses that omit
        // category translations.
        return categoryNames.includes(expectedCategoryName)
            || (categoryNames.length === 0 && categoryId === roomCategoryId);
    });
}

const getPointCoordinates = (point: any) => {
    const pointValue = point?.lat_long ?? point?.location ?? point?.geometry;
    if (typeof pointValue !== 'string') return null;

    const coordinates = pointValue.match(/POINT\s*\(\s*([^\s]+)\s+([^\s)]+)\s*\)/i);
    if (!coordinates) return null;

    const longitude = Number(coordinates[1]);
    const latitude = Number(coordinates[2]);
    return Number.isFinite(latitude) && Number.isFinite(longitude) ? { latitude, longitude } : null;
}

const getImageMapCoordinates = (value: unknown) => {
    const match = String(value ?? '').match(/POINT\s*\(\s*([-+]?\d*\.?\d+)\s+([-+]?\d*\.?\d+)\s*\)/i);
    if (!match) return null;
    const x = Number(match[1]);
    const y = Number(match[2]);
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
}

const findCategoryInTree = (categories: any[], categoryId: number): any => {
    for (const category of categories) {
        if (Number(category?.category_id) === categoryId) return category;
        const match = findCategoryInTree(category?.children ?? [], categoryId);
        if (match) return match;
    }
    return undefined;
}

const getPointCategoryIds = (point: any) => (point?.categories ?? [])
    .map((line: any) => Number(line?.category_id ?? line?.category?.category_id))
    .filter(Number.isFinite);

const getFloorCategory = (point: any, categoryTree: any[]) => {
    const categoryIds = getPointCategoryIds(point);
    return categoryTree.find((floor: any) => categoryIds.some((categoryId: number) => findCategoryInTree([floor], categoryId))) ?? null;
}

const PoiFloorMapModal = ({ visible, point, findifyDataContext, onClose }: any) => {
    const colors = PlaceColors(findifyDataContext);
    const [floor, setFloor] = useState<any>(null);
    const [floorPoints, setFloorPoints] = useState<any[]>([]);
    const [imageLayout, setImageLayout] = useState({ width: 0, height: 0 });
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const floorImage = getFeaturedImageUrl(floor);

    useEffect(() => {
        if (!visible) return;
        setFloor(null);
        setFloorPoints([]);
        setImageLayout({ width: 0, height: 0 });
        setImageSize({ width: 0, height: 0 });
        const categoriesApi = getApiFromModule('categories', findifyDataContext);
        const pointsApi = getApiFromModule('points_of_interest', findifyDataContext);
        const routesApi = getApiFromModule('routes', findifyDataContext);
        Promise.all([
            categoriesApi?.getVisitTree?.('points_of_interest').catch(() => categoriesApi?.getList(0, 100, null, { category_type: 'points_of_interest' }, null)),
            pointsApi?.getList(0, 250, null, null, null),
            routesApi?.getList(0, 100, null, {}, null),
            // QR navigation only supplies an id. Fetch the complete POI so
            // its indoor-map coordinates and category references are always
            // available, even when it is outside the first public list page.
            Promise.resolve(pointsApi?.get?.(point?.point_of_interest_id, null, null)).catch(() => null),
        ]).then(async ([categoriesData, pointsData, routesData, pointDetails]: any[]) => {
            const categoryTree = categoriesData?.results ?? [];
            const mapPoint = {
                ...point,
                ...pointDetails,
                x_y_cord: pointDetails?.x_y_cord ?? point?.x_y_cord,
                categories: pointDetails?.categories?.length ? pointDetails.categories : point?.categories,
            };
            const pointFloor = getFloorCategory(mapPoint, categoryTree);
            if (!pointFloor) {
                setFloor(null);
                setFloorPoints([]);
                return;
            }

            // The visit tree is intentionally lightweight and can omit the
            // floor's featured image. Load the full category, just as the
            // guided visit does, before presenting the floor plan.
            const fullFloor = await categoriesApi?.get(pointFloor.category_id, null, null).catch(() => null);
            setFloor(fullFloor ? { ...pointFloor, ...fullFloor } : pointFloor);

            const routeSummaries = (routesData?.results ?? [])
                .filter((route: any) => isPublishedRoute(route) && belongsToSelectedVisit(route, findifyDataContext?.visitVersion));
            const routeDetails = await Promise.all(routeSummaries.map((route: any) => (
                routesApi?.get(route?.route_id ?? route?.page_slug, null, null).catch(() => null)
            )));
            const candidatesById = new Map<number, any>();
            [...(pointsData?.results ?? []), ...routeDetails.flatMap((route: any) => (
                (route?.points_of_interest ?? []).map((routePoint: any) => routePoint?.point_of_interest ?? routePoint)
            ))].forEach((candidate: any) => {
                const candidateId = Number(candidate?.point_of_interest_id);
                if (candidateId) candidatesById.set(candidateId, { ...candidatesById.get(candidateId), ...candidate });
            });
            // The POI opened by QR is not guaranteed to be in the paginated
            // public list, so ensure it is always present and highlighted.
            const currentPointId = Number(point.point_of_interest_id);
            const knownCurrentPoint = candidatesById.get(currentPointId);
            candidatesById.set(currentPointId, {
                ...knownCurrentPoint,
                ...mapPoint,
                x_y_cord: mapPoint?.x_y_cord ?? knownCurrentPoint?.x_y_cord,
                categories: mapPoint?.categories?.length ? mapPoint.categories : knownCurrentPoint?.categories,
            });
            setFloorPoints(Array.from(candidatesById.values()).filter((candidate: any) => (
                !!getImageMapCoordinates(candidate?.x_y_cord)
                && Number(getFloorCategory(candidate, categoryTree)?.category_id) === Number(pointFloor.category_id)
            )));
        }).catch(() => {
            setFloor(null);
            setFloorPoints([]);
        });
    }, [visible, point?.point_of_interest_id, findifyDataContext?.workspace?.workspace_id]);

    // React Native Web does not consistently expose the source dimensions in
    // Image.onLoad. Read them separately so the floor-plan markers can be
    // positioned on the first time the modal opens.
    useEffect(() => {
        if (!floorImage) {
            setImageSize({ width: 0, height: 0 });
            return;
        }
        Image.getSize(
            floorImage,
            (width, height) => setImageSize({ width, height }),
            () => setImageSize({ width: 0, height: 0 }),
        );
    }, [floorImage]);

    const scale = imageLayout.width && imageLayout.height && imageSize.width && imageSize.height
        ? Math.min(imageLayout.width / imageSize.width, imageLayout.height / imageSize.height)
        : 0;
    const renderMarker = (mapPoint: any) => {
        const coordinates = getImageMapCoordinates(mapPoint?.x_y_cord);
        if (!coordinates || scale <= 0) return null;
        const isCurrent = Number(mapPoint?.point_of_interest_id) === Number(point?.point_of_interest_id);
        const size = isCurrent ? 42 : 28;
        return <View key={mapPoint.point_of_interest_id} style={[styles.floorMapMarker, {
            left: (imageLayout.width - imageSize.width * scale) / 2 + coordinates.x * scale - size / 2,
            top: (imageLayout.height - imageSize.height * scale) / 2 + coordinates.y * scale - size,
        }]}>
            <Icon name="location" size={size} color={isCurrent ? colors.primary.get() : '#777'} />
        </View>;
    }

    return <Modal visible={visible} transparent={true} animationType="fade" onRequestClose={onClose}>
        <View style={styles.floorMapModalBackdrop}>
            <View style={[styles.floorMapModal, { backgroundColor: colors.background.get() }]}>
                <Pressable style={[styles.floorMapClose, { backgroundColor: colors.primary.get() }]} onPress={onClose}>
                    <Icon name="close-outline" size={28} color="#fff" />
                </Pressable>
                <PlaceText style={[styles.floorMapTitle, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, 'Localização no mapa', 'Location on map', 'Emplacement sur le plan')}</PlaceText>
                <PlaceText style={[styles.floorMapSubtitle, { color: colors.text.withAlpha(180) }]}>{getTranslatedString(floor?.name, findifyDataContext)}</PlaceText>
                {floorImage ? <View key={floorImage} style={styles.floorMapImage} onLayout={({ nativeEvent }) => setImageLayout(nativeEvent.layout)}>
                    <Image
                        source={{ uri: floorImage }}
                        resizeMode="contain"
                        style={styles.floorMapImageFill}
                        onLoad={({ nativeEvent }) => {
                            const source = nativeEvent?.source;
                            if (source?.width && source?.height) {
                                setImageSize({ width: source.width, height: source.height });
                            }
                        }}
                    />
                    {floorPoints.map(renderMarker)}
                </View> : <View style={styles.floorMapEmpty}>
                    <PlaceText style={{ color: colors.text.get() }}>{localizedText(findifyDataContext, 'Mapa indisponível para este ponto.', 'Map unavailable for this point.', 'Plan indisponible pour ce point.')}</PlaceText>
                </View>}
                <View style={styles.floorMapLegend}>
                    <Icon name="location" size={25} color={colors.primary.get()} />
                    <PlaceText style={[styles.floorMapLegendText, { color: colors.text.get() }]}>{localizedText(findifyDataContext, 'Este ponto', 'This point', 'Ce point')}</PlaceText>
                    <Icon name="location" size={22} color="#777" />
                    <PlaceText style={[styles.floorMapLegendText, { color: colors.text.get() }]}>{localizedText(findifyDataContext, 'Outros pontos', 'Other points', 'Autres points')}</PlaceText>
                </View>
            </View>
        </View>
    </Modal>
}

const isReviewContent = (content: any) => {
    return content?.categories?.some((line: any) => line?.category_id === 110 || line?.category?.category_id === 110 || line?.category?.page_slug === 'review');
}

const getReviewFormId = (content: any) => {
    const description = Object.values(content?.description ?? {}).join(' ');
    const match = description.match(/data-lexical-form-id=["'](\d+)["']/);
    return match?.[1] ?? '40';
}

const ratingIcons = [
    'sad-outline',
    'sad',
    'remove-circle-outline',
    'happy-outline',
    'happy',
];

const BiscainhosReviewDetails = ({ content, navigation, findifyDataContext, bottomButtons }: any) => {
    const { width, height } = useWindowDimensions();
    const isTablet = width >= 768;
    const isLandscapeTablet = isTablet && width > height;
    const [formData, setFormData] = useState<any>(null);
    const [ratings, setRatings] = useState<Record<string, string | number>>({});
    const [submitted, setSubmitted] = useState(false);
    const formId = getReviewFormId(content);
    const selectedLanguage = findifyDataContext?.language ?? 'pt-PT';
    useEffect(() => {
        const formsApi = getApiFromModule('forms', findifyDataContext);
        formsApi?.get(formId, null).then((form: any) => {
            form?.questions?.sort((qA: any, qB: any) => Number(qA.sequence ?? 0) - Number(qB.sequence ?? 0));
            setFormData(form);
        }).catch((error: any) => {
            console.error('Failed to load review form:', error);
        });
    }, [formId, findifyDataContext?.workspace?.workspace_id]);

    const submitReview = () => {
        if (!formData) return;
        const formsApi = getApiFromModule('forms', findifyDataContext);
        const responseLanguage = selectedLanguage || 'pt-PT';
        const questionResponses = (formData.questions ?? [])
            .filter((question: any) => ratings[question.form_question_id] !== undefined)
            .map((question: any) => {
                const response = ratings[question.form_question_id];

                return {
                    form_question_id: question.form_question_id,
                    language: responseLanguage,
                    // The custom rating UI uses a 1–5 faces scale. Keep its
                    // numeric value so the backoffice renders the rating, not a choice label.
                    response,
                    choices: [],
                };
            });

        formsApi?.create({
            form_id: formId,
            language: responseLanguage,
            question_responses: questionResponses,
        }, null).then(() => {
            setSubmitted(true);
        });
    }

    return <>
        <ScrollView
            bounces={false}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            style={styles.reviewPage}>
            <BiscainhosAppHeader findifyDataContext={findifyDataContext} navigation={navigation} />
            <View style={[
                styles.reviewContent,
                isTablet ? styles.reviewContentTablet : null,
                isLandscapeTablet ? styles.reviewContentLandscapeTablet : null,
            ]}>
                <View style={styles.reviewTitleBlock}>
                    <PlaceText style={styles.reviewTitle}>{localizedText(findifyDataContext, 'Avaliação', 'Review', 'Évaluation')}</PlaceText>
                    <PlaceText style={styles.reviewLead}>{localizedText(findifyDataContext, 'A sua opinião é valiosa para nós.', 'Your feedback is valuable to us.', 'Votre avis est précieux pour nous.')}</PlaceText>
                </View>

                {(formData?.questions ?? []).map((question: any, index: number) => {
                    const selectedValue = ratings[question.form_question_id];
                    const isParagraph = question.question_type === 'paragraph' || question.question_type === 'text';
                    const questionText = question.question?.[selectedLanguage]
                        ?? (isEnglishLanguage(selectedLanguage) ? question.question?.['en-US'] ?? question.question?.['en-GB'] : question.question?.['pt-PT'])
                        ?? getTranslatedString(question.question, findifyDataContext);
                    return <View key={question.form_question_id} style={styles.reviewQuestionCard}>
                        <PlaceText style={styles.reviewQuestionTitle}>{index + 1}. {questionText}</PlaceText>
                        {isParagraph ? <TextInput
                            multiline
                            textAlignVertical="top"
                            value={typeof selectedValue === 'string' ? selectedValue : ''}
                            onChangeText={(value) => setRatings((current) => ({
                                ...current,
                                [question.form_question_id]: value,
                            }))}
                            placeholder={localizedText(findifyDataContext, 'Escreva aqui a sua resposta…', 'Write your response here…', 'Écrivez votre réponse ici…')}
                            placeholderTextColor="#806e69"
                            style={styles.reviewTextInput}
                        /> : <View style={styles.reviewRatingRow}>
                            {ratingIcons.map((iconName, ratingIndex) => {
                                const value = ratingIndex + 1;
                                const selected = selectedValue === value;
                                return <Pressable
                                    key={iconName + value}
                                    style={styles.reviewRatingButton}
                                    onPress={() => setRatings((current) => ({
                                        ...current,
                                        [question.form_question_id]: value,
                                    }))}>
                                    <Icon
                                        name={iconName}
                                        size={36}
                                        color={selected ? '#990000' : '#5b332f'}
                                        style={{ opacity: selected ? 1 : 0.82 }}
                                    />
                                </Pressable>
                            })}
                        </View>}
                    </View>
                })}

                <Pressable style={styles.reviewSubmitButton} onPress={submitReview}>
                    <PlaceText style={styles.reviewSubmitText}>{submitted ? localizedText(findifyDataContext, 'AVALIAÇÃO SUBMETIDA', 'REVIEW SUBMITTED', 'ÉVALUATION ENVOYÉE') : localizedText(findifyDataContext, 'SUBMETER AVALIAÇÃO', 'SUBMIT REVIEW', "ENVOYER L'ÉVALUATION")}</PlaceText>
                </Pressable>

                {findifyDataContext?.workspace?.app_settings?.app_banner_url ? (
                    <Image
                        source={{ uri: findifyDataContext.workspace.app_settings.app_banner_url }}
                        resizeMode="cover"
                        style={styles.reviewBottomImage}
                    />
                ) : null}
                <BiscainhosFooterLogo findifyDataContext={findifyDataContext} />
            </View>
        </ScrollView>
        {bottomButtons}
    </>
}

const BiscainhosGenericContentDetails = ({
    content,
    image,
    title,
    resume,
    description,
    navigation,
    findifyDataContext,
    videoUrl,
    audioUrl,
    bottomButtons,
    t,
}: any) => {
    const colors = PlaceColors(findifyDataContext);
    const { width, height } = useWindowDimensions();
    const isTablet = width >= 768;
    const isLandscapeTablet = isTablet && width > height;
    const displayTitle = titleCase(title || getTranslatedString(content?.title ?? content?.name, findifyDataContext) || '');
    const cleanResume = stripHtml(resume);
    const model3dUrl = getContent3dUrl(content);

    return <>
        <ScrollView
            bounces={false}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            style={[styles.biscainhosPoiPage, { backgroundColor: colors.background.get() }]}>
            <BiscainhosAppHeader findifyDataContext={findifyDataContext} navigation={navigation} />

            {image ? (
                <View style={[
                    styles.biscainhosPoiHero,
                    isTablet ? styles.biscainhosPoiHeroTablet : null,
                    isLandscapeTablet ? styles.biscainhosPoiHeroLandscapeTablet : null,
                ]}>
                    <Image source={{ uri: image }} resizeMode="cover" style={styles.biscainhosPoiHeroImage} />
                    {videoUrl ? (
                        <View style={[styles.biscainhosHeroPlayHint, { backgroundColor: colors.secondary.get(), borderColor: colors.background.get() }]}>
                            <Icon name="play" size={42} color={colors.primary.get()} />
                        </View>
                    ) : null}
                </View>
            ) : null}

            <View style={[
                styles.biscainhosPoiContent,
                isTablet ? styles.biscainhosPoiContentTablet : null,
                isLandscapeTablet ? styles.biscainhosPoiContentLandscapeTablet : null,
            ]}>
                <View style={[styles.biscainhosPoiTitleCard, { borderLeftColor: colors.primary.get(), backgroundColor: colors.background.solidAlphaBlackBase(18) }]}>
                    <PlaceText style={[styles.biscainhosPoiTitle, { color: colors.primary.get() }]}>{displayTitle}</PlaceText>
                    {!!cleanResume && <PlaceText style={styles.biscainhosPoiSubtitle}>{cleanResume}</PlaceText>}
                </View>

                {videoUrl ? <ContentVideoPlayer videoUrl={videoUrl} /> : null}

                <SimpleAudioPlayer audioUrl={audioUrl} findifyDataContext={findifyDataContext} title={localizedText(findifyDataContext, 'Audioguia', 'Audio Guide', 'Audioguide')} />

                {model3dUrl ? (
                    <Pressable
                        style={[styles.poiModelButton, { borderColor: colors.primary.get(), backgroundColor: colors.background.solidAlphaBlackBase(10) }]}
                        onPress={() => {
                            if (Platform.OS === 'web' && typeof window !== 'undefined') {
                                window.open(model3dUrl, '_blank', 'noopener,noreferrer');
                                return;
                            }
                            Linking.openURL(model3dUrl);
                        }}>
                        <Icon name="cube-outline" size={25} color={colors.primary.get()} />
                        <PlaceText style={[styles.poiModelButtonText, { color: colors.primary.get() }]}>
                            {localizedText(findifyDataContext, 'Explorar modelo 3D', 'Explore 3D model', 'Explorer le modèle 3D')}
                        </PlaceText>
                        <Icon name="open-outline" size={20} color={colors.primary.get()} />
                    </Pressable>
                ) : null}

                {!!description && <View style={styles.biscainhosPoiLanguageSection}>
                    <View style={styles.biscainhosPoiLanguageTitleRow}>
                        <Icon name="language-outline" size={18} color={colors.secondary.get()} />
                        <PlaceText style={[styles.biscainhosPoiLanguageTitle, { color: colors.primary.get() }]}>
                            {localizedText(findifyDataContext, 'PORTUGUÊS', 'ENGLISH', 'FRANÇAIS')}
                        </PlaceText>
                    </View>
                    <FindifyHTMLRender
                        baseStyles={{
                            color: colors.text.get(),
                            fontSize: 17,
                            lineHeight: 27,
                        }}
                        tagsStyles={{ p: { marginBottom: 10 } }}
                        classesStyles={{ PlaygroundEditorTheme__paragraph: { marginBottom: 10 } }}
                        htmlCode={description}
                    />
                </View>}

                {content.galleries ? (
                    <View style={styles.biscainhosDetailsGallery}>
                        <ContentDetailsGallery findifyDataContext={findifyDataContext} t={t} content={content} navigation={navigation} />
                    </View>
                ) : null}
                <BiscainhosFooterLogo findifyDataContext={findifyDataContext} />
            </View>
        </ScrollView>
        {bottomButtons}
    </>
}

const BiscainhosPoiDetails = ({ content, image, navigation, findifyDataContext, videoUrl, audioUrl, bottomButtons, t }: any) => {
    const colors = PlaceColors(findifyDataContext);
    const { width, height } = useWindowDimensions();
    const isTablet = width >= 768;
    const isLandscapeTablet = isTablet && width > height;
    const [videoOpen, setVideoOpen] = useState(false);
    const [floorMapOpen, setFloorMapOpen] = useState(false);
    const [roomInfoOpen, setRoomInfoOpen] = useState(false);
    const [roomGalleryImages, setRoomGalleryImages] = useState<string[]>([]);
    const [roomGalleryImageIndex, setRoomGalleryImageIndex] = useState<number | null>(null);
    const [previousPoint, setPreviousPoint] = useState<any>(null);
    const [nextPoint, setNextPoint] = useState<any>(null);
    const [roomObjects, setRoomObjects] = useState<any[]>([]);
    const [hasRoomObjectsSection, setHasRoomObjectsSection] = useState(false);
    const model3dUrl = getContent3dUrl(content);
    const title = titleCase(getTranslatedString(content?.title ?? content?.name, findifyDataContext) || '');
    const descriptionHtml = getTranslatedString(content?.description, findifyDataContext) || '';
    const description = stripHtml(descriptionHtml);
    const arPointIds = findifyDataContext?.visitVersion === 'accessible' ? [749, 750, 754] : [700, 701, 708];
    const hasAugmentedReality = arPointIds.includes(Number(content?.point_of_interest_id));
    useEffect(() => {
        const api = getApiFromModule('routes', findifyDataContext);
        api?.getList(0, 100, null, {}, null)
            .then((res: any) => {
                const routeSummaries = (res?.results ?? [])
                    .filter((route: any) => isPublishedRoute(route) && belongsToSelectedVisit(route, findifyDataContext?.visitVersion))
                    .sort(compareRoutesByPublication);

                return Promise.all(routeSummaries.map((route: any) => {
                    const routeLookup = route?.route_id ?? route?.page_slug;
                    return api?.get(routeLookup, null, null)
                        .catch(() => null);
                }));
            })
            .then((routes: any[]) => {
                const routePoints = routes
                    .filter(Boolean)
                    .flatMap((route: any) => (route?.points_of_interest ?? [])
                        .slice()
                        .sort((a: any, b: any) => Number(a.sequence ?? 0) - Number(b.sequence ?? 0)));
                const currentIndex = routePoints.findIndex((routePoint: any) => routePoint?.point_of_interest?.point_of_interest_id === content?.point_of_interest_id);
                setPreviousPoint(currentIndex > 0 ? routePoints[currentIndex - 1]?.point_of_interest ?? null : null);
                setNextPoint(currentIndex >= 0 ? routePoints[currentIndex + 1]?.point_of_interest ?? null : null);
            })
            .catch(() => {
                setPreviousPoint(null);
                setNextPoint(null);
            });
    }, [content?.point_of_interest_id, findifyDataContext?.workspace?.workspace_id, findifyDataContext?.visitVersion]);

    useEffect(() => {
        let cancelled = false;
        const roomSlug = content?.page_slug;
        if (!roomSlug) {
            setRoomObjects([]);
            setHasRoomObjectsSection(false);
            return;
        }

        const categoriesApi = getApiFromModule('categories', findifyDataContext);
        const pointsApi = getApiFromModule('points_of_interest', findifyDataContext);

        setRoomObjects([]);
        setHasRoomObjectsSection(false);

        categoriesApi?.getList(0, 250, null, { category_type: 'points_of_interest', active: 1 }, null)
            .then((categoriesData: any) => {
                const roomCategory = findRoomObjectsCategory(categoriesData?.results ?? [], roomSlug);
                if (!roomCategory?.category_id) {
                    return null;
                }

                if (!cancelled) setHasRoomObjectsSection(true);

                // Do not rely only on the nested API filter: some public API
                // responses ignore it and return the same first POIs for every
                // room. Check each POI's category relationship explicitly.
                return pointsApi?.getList(0, 250, null, null, null)
                    .then((pointsData: any) => ({ pointsData, roomCategoryId: roomCategory.category_id }));
            })
            .then((result: any) => {
                if (!result?.pointsData?.results || cancelled) return;
                const roomCategoryId = Number(result.roomCategoryId);
                const objects = result.pointsData.results.filter((item: any) => {
                    const belongsToRoom = isRoomObject(item, roomCategoryId, roomSlug);
                    return belongsToRoom && item?.point_of_interest_id !== content?.point_of_interest_id;
                });
                setRoomObjects(objects);
            })
            .catch((error: any) => {
                console.error('Failed to load room featured objects:', error);
                if (!cancelled) {
                    setRoomObjects([]);
                    setHasRoomObjectsSection(false);
                }
            });

        return () => { cancelled = true; };
    }, [content?.page_slug, content?.point_of_interest_id, findifyDataContext?.workspace?.workspace_id]);

    const openPoint = (point: any) => {
        if (!point?.point_of_interest_id) return;
        navigation.push("PlaceContentDetails", {
            module: "points_of_interest",
            content_id: point.point_of_interest_id,
            page_slug: point.page_slug,
        });
    }
    const pointCoordinates = getPointCoordinates(content);
    const hasFloorMapLocation = typeof content?.x_y_cord === 'string' && /POINT\s*\(/i.test(content.x_y_cord);
    const hasMapLocation = !!pointCoordinates || hasFloorMapLocation;
    const openMap = () => {
        if (!hasMapLocation) return;
        if (!pointCoordinates) {
            setFloorMapOpen(true);
            return;
        }
        navigation.push('PlaceMap', {
            point_of_interest_id: content.point_of_interest_id,
            longitude: pointCoordinates.longitude,
            latitude: pointCoordinates.latitude,
        });
    }

    return <>
        <ScrollView
            bounces={false}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            style={[styles.biscainhosPoiPage, { backgroundColor: colors.background.get() }]}>
            <BiscainhosAppHeader findifyDataContext={findifyDataContext} navigation={navigation} />

            <View style={[
                styles.biscainhosPoiHero,
                isTablet ? styles.biscainhosPoiHeroTablet : null,
                isLandscapeTablet ? styles.biscainhosPoiHeroLandscapeTablet : null,
            ]}>
                {image ? <Image source={{ uri: image }} resizeMode="cover" style={styles.biscainhosPoiHeroImage} /> : null}
                {videoUrl ? (
                    <Pressable style={[styles.biscainhosPoiPlayButton, { backgroundColor: colors.secondary.get(), borderColor: colors.background.get() }]} onPress={() => setVideoOpen(true)}>
                        <Icon name="play" size={42} color={colors.primary.get()} />
                    </Pressable>
                ) : null}
            </View>

            <View style={[
                styles.biscainhosPoiContent,
                isTablet ? styles.biscainhosPoiContentTablet : null,
                isLandscapeTablet ? styles.biscainhosPoiContentLandscapeTablet : null,
            ]}>
                <View style={[styles.biscainhosPoiTitleCard, { borderLeftColor: colors.primary.get(), backgroundColor: colors.background.solidAlphaBlackBase(18) }]}>
                    <View style={styles.biscainhosPoiTitleRow}>
                        <PlaceText style={[styles.biscainhosPoiTitle, styles.biscainhosPoiTitleWithActions, { color: colors.primary.get() }]}>{title}</PlaceText>
                        {hasMapLocation ? <Pressable
                            style={[styles.poiQuickAction, { borderColor: colors.primary.withAlpha(70) }]}
                            onPress={openMap}
                            accessibilityRole="button"
                            accessibilityLabel={localizedText(findifyDataContext, 'Abrir no mapa', 'Open map', 'Ouvrir la carte')}>
                            <Icon name="map-outline" size={24} color={colors.primary.get()} />
                        </Pressable> : null}
                        {model3dUrl ? <Pressable
                            style={[styles.poiQuickAction, { borderColor: colors.primary.withAlpha(70) }]}
                            onPress={() => {
                                if (Platform.OS === 'web' && typeof window !== 'undefined') {
                                    window.open(model3dUrl, '_blank', 'noopener,noreferrer');
                                    return;
                                }
                                Linking.openURL(model3dUrl);
                            }}
                            accessibilityRole="button"
                            accessibilityLabel={localizedText(findifyDataContext, 'Abrir modelo 3D', 'Open 3D model', 'Ouvrir le modèle 3D')}>
                            <Icon name="cube-outline" size={24} color={colors.primary.get()} />
                        </Pressable> : null}
                    </View>
                </View>

                <SimpleAudioPlayer audioUrl={audioUrl} findifyDataContext={findifyDataContext} title={localizedText(findifyDataContext, 'Audioguia', 'Audio Guide', 'Audioguide')} />

                {hasAugmentedReality ? <Pressable
                    style={[styles.poiArCard, { backgroundColor: colors.primary.get(), borderColor: colors.secondary.get() }]}
                    onPress={() => navigation.push('ScanRA')}>
                    <View style={[styles.poiArIcon, { backgroundColor: colors.secondary.get() }]}>
                        <Icon name="scan-outline" size={27} color={colors.primary.get()} />
                    </View>
                    <View style={styles.poiArCopy}>
                        <PlaceText style={styles.poiArTitle}>{localizedText(findifyDataContext, 'Experiência de Realidade Aumentada', 'Augmented Reality experience', 'Expérience de réalité augmentée')}</PlaceText>
                        <PlaceText style={styles.poiArText}>{localizedText(findifyDataContext, 'Abra a câmara para ver o vídeo deste ponto.', 'Open the camera to view this point’s video.', 'Ouvrez la caméra pour voir la vidéo de ce point.')}</PlaceText>
                    </View>
                    <Icon name="chevron-forward-outline" size={25} color="#fff" />
                </Pressable> : null}

                {hasRoomObjectsSection && !!description ? (
                    <Pressable style={[styles.roomInfoLinkCard, { borderColor: colors.primary.withAlpha(32), backgroundColor: colors.background.solidAlphaBlackBase(14) }]} onPress={() => setRoomInfoOpen(true)}>
                        <View style={[styles.roomInfoLinkIcon, { backgroundColor: colors.primary.get() }]}>
                            <Icon name="information-circle-outline" size={26} color="#fff" />
                        </View>
                        <PlaceText style={[styles.roomInfoLinkText, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, 'Informações da sala', 'Room information', 'Informations sur la salle')}</PlaceText>
                        <Icon name="chevron-forward-outline" size={24} color={colors.primary.get()} />
                    </Pressable>
                ) : null}

                {!!description && !hasRoomObjectsSection ? <View style={styles.biscainhosPoiLanguageSection}>
                    <View style={styles.biscainhosPoiLanguageTitleRow}>
                        <Icon name="globe-outline" size={18} color={colors.secondary.get()} />
                        <PlaceText style={[styles.biscainhosPoiLanguageTitle, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, 'PORTUGUÊS', 'ENGLISH', 'FRANÇAIS')}</PlaceText>
                    </View>
                    <FindifyHTMLRender
                        baseStyles={{
                            color: colors.text.get(),
                            fontSize: 17,
                            lineHeight: 27,
                        }}
                        tagsStyles={{ p: { marginBottom: 10 } }}
                        classesStyles={{ PlaygroundEditorTheme__paragraph: { marginBottom: 10 } }}
                        htmlCode={descriptionHtml}
                    />
                </View> : null}

                {(content?.galleries?.length ?? 0) > 0 ? (
                    <View style={styles.poiGallerySection}>
                        <View style={styles.biscainhosPoiLanguageTitleRow}>
                            <Icon name="images-outline" size={20} color={colors.secondary.get()} />
                            <PlaceText style={[styles.biscainhosPoiLanguageTitle, { color: colors.primary.get() }]}>
                                {localizedText(findifyDataContext, 'GALERIA', 'GALLERY', 'GALERIE')}
                            </PlaceText>
                        </View>
                        <ContentDetailsGallery
                            findifyDataContext={findifyDataContext}
                            t={t}
                            content={content}
                            navigation={navigation}
                            onImagePress={(images: string[], index: number) => {
                                setRoomGalleryImages(images);
                                setRoomGalleryImageIndex(index);
                            }}
                        />
                    </View>
                ) : null}

                {roomObjects.length > 0 ? (
                    <View style={styles.featuredCollectionSection}>
                        <View style={styles.featuredCollectionTitleRow}>
                            <View style={[styles.featuredCollectionLine, { backgroundColor: colors.secondary.withAlpha(80) }]} />
                            <PlaceText style={[styles.featuredCollectionTitle, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, 'Acervo em destaque', 'Featured collection', 'Collection à la une')}</PlaceText>
                            <View style={[styles.featuredCollectionLine, { backgroundColor: colors.secondary.withAlpha(80) }]} />
                        </View>
                        {roomObjects.map((object: any) => {
                            const objectImage = getFeaturedImageUrl(object);
                            const objectTitle = makeContentTitle(object, 'points_of_interest', findifyDataContext);
                            return <Pressable
                                key={`room_object_${object.point_of_interest_id}`}
                                style={styles.featuredObjectRow}
                                onPress={() => openPoint(object)}>
                                <View style={styles.featuredObjectThumbWrap}>
                                    {objectImage ? <Image source={{ uri: objectImage }} resizeMode="cover" style={styles.featuredObjectThumb} /> : null}
                                </View>
                                <PlaceText numberOfLines={2} style={[styles.featuredObjectTitle, { color: colors.text.withAlpha(220) }]}>{objectTitle}</PlaceText>
                                <Icon name="chevron-forward-circle" size={32} color={colors.text.withAlpha(150)} />
                            </Pressable>
                        })}
                    </View>
                ) : null}

                {previousPoint || nextPoint ? (
                    <View style={styles.biscainhosPoiNavigation}>
                    {nextPoint ? (
                        <Pressable style={[styles.biscainhosPoiNextCard, { backgroundColor: colors.primary.get(), borderColor: colors.secondary.get() }]} onPress={() => openPoint(nextPoint)}>
                            <View style={{ flex: 1 }}>
                                <PlaceText style={styles.biscainhosPoiNextTitle}>{localizedText(findifyDataContext, 'Próxima Sala', 'Next Room', 'Salle suivante')}</PlaceText>
                                <PlaceText style={styles.biscainhosPoiNextCopy}>
                                    {localizedText(findifyDataContext, 'Sugestão: ', 'Suggested: ', 'Suggestion : ')}
                                    <PlaceText style={styles.biscainhosPoiNextCopyStrong}>{titleCase(getTranslatedString(nextPoint?.title ?? nextPoint?.name, findifyDataContext))}</PlaceText>
                                </PlaceText>
                            </View>
                            <View style={[styles.biscainhosPoiNextIcon, { backgroundColor: colors.secondary.get() }]}>
                                <Icon name="arrow-forward-outline" size={34} color={colors.primary.get()} />
                            </View>
                        </Pressable>
                    ) : null}
                    {previousPoint ? <Pressable style={[styles.biscainhosPoiNextCard, styles.biscainhosPoiPreviousCard, { borderColor: colors.primary.withAlpha(90), backgroundColor: colors.background.solidAlphaBlackBase(12) }]} onPress={() => openPoint(previousPoint)}>
                        <View style={[styles.biscainhosPoiNextIcon, { backgroundColor: colors.secondary.get() }]}>
                            <Icon name="arrow-back-outline" size={34} color={colors.primary.get()} />
                        </View>
                        <View style={{ flex: 1 }}>
                            <PlaceText style={[styles.biscainhosPoiNextTitle, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, 'Sala anterior', 'Previous room', 'Salle précédente')}</PlaceText>
                            <PlaceText style={[styles.biscainhosPoiNextCopy, { color: colors.text.withAlpha(180) }]}>
                                {localizedText(findifyDataContext, 'Voltar a: ', 'Back to: ', 'Retour à : ')}
                                <PlaceText style={[styles.biscainhosPoiNextCopyStrong, { color: colors.primary.get() }]}>{titleCase(getTranslatedString(previousPoint?.title ?? previousPoint?.name, findifyDataContext))}</PlaceText>
                            </PlaceText>
                        </View>
                    </Pressable> : null}
                    </View>
                ) : null}
                <BiscainhosFooterLogo findifyDataContext={findifyDataContext} />
            </View>
        </ScrollView>

        <Modal visible={videoOpen} transparent={true} animationType="fade" onRequestClose={() => setVideoOpen(false)}>
            <View style={styles.biscainhosVideoModal}>
                <Pressable style={styles.biscainhosVideoClose} onPress={() => setVideoOpen(false)}>
                    <Icon name="close-outline" size={34} color="#fff" />
                </Pressable>
                {videoUrl ? <ContentVideoPlayer videoUrl={videoUrl} /> : null}
            </View>
        </Modal>
        <PoiFloorMapModal
            visible={floorMapOpen}
            point={content}
            findifyDataContext={findifyDataContext}
            onClose={() => setFloorMapOpen(false)}
        />
        <Modal visible={roomInfoOpen} transparent={false} animationType="slide" onRequestClose={() => setRoomInfoOpen(false)}>
            <View style={[styles.roomInfoModalPage, { backgroundColor: colors.background.get() }]}>
                <BiscainhosAppHeader findifyDataContext={findifyDataContext} navigation={navigation} showAccessibility={false} />
                <Pressable style={[styles.roomInfoModalClose, { backgroundColor: colors.primary.get() }]} onPress={() => setRoomInfoOpen(false)}>
                    <Icon name="close-outline" size={30} color="#fff" />
                </Pressable>
                <ScrollView
                    bounces={false}
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={[
                        styles.roomInfoModalContent,
                        isTablet ? styles.roomInfoModalContentTablet : null,
                    ]}>
                    <PlaceText style={[styles.roomInfoModalTitle, { color: colors.primary.get() }]}>{title}</PlaceText>
                    {image ? (
                        <Image
                            source={{ uri: image }}
                            resizeMode="cover"
                            style={styles.roomInfoModalFeaturedImage}
                        />
                    ) : null}
                    <View style={styles.biscainhosPoiLanguageSection}>
                        <View style={styles.biscainhosPoiLanguageTitleRow}>
                            <Icon name="globe-outline" size={18} color={colors.secondary.get()} />
                            <PlaceText style={[styles.biscainhosPoiLanguageTitle, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, 'PORTUGUÊS', 'ENGLISH', 'FRANÇAIS')}</PlaceText>
                        </View>
                        <FindifyHTMLRender
                            baseStyles={{
                                color: colors.text.get(),
                                fontSize: 17,
                                lineHeight: 27,
                            }}
                            tagsStyles={{ p: { marginBottom: 10 } }}
                            classesStyles={{ PlaygroundEditorTheme__paragraph: { marginBottom: 10 } }}
                            htmlCode={descriptionHtml}
                        />
                    </View>
                    {videoUrl ? <ContentVideoPlayer videoUrl={videoUrl} /> : null}
                    <SimpleAudioPlayer
                        audioUrl={audioUrl}
                        findifyDataContext={findifyDataContext}
                        title={localizedText(findifyDataContext, 'Audioguia', 'Audio Guide', 'Audioguide')}
                    />
                    {model3dUrl ? (
                        <Pressable
                            style={[styles.roomInfoModalModelButton, { borderColor: colors.primary.get() }]}
                            onPress={() => {
                                if (Platform.OS === 'web' && typeof window !== 'undefined') {
                                    window.open(model3dUrl, '_blank', 'noopener,noreferrer');
                                    return;
                                }
                                Linking.openURL(model3dUrl);
                            }}>
                            <Icon name="cube-outline" size={24} color={colors.primary.get()} />
                            <PlaceText style={[styles.roomInfoModalModelButtonText, { color: colors.primary.get() }]}>
                                {localizedText(findifyDataContext, 'Abrir modelo 3D', 'Open 3D model', 'Ouvrir le modèle 3D')}
                            </PlaceText>
                        </Pressable>
                    ) : null}
                    {(content?.galleries?.length ?? 0) > 0 ? (
                        <View style={styles.roomInfoModalGallery}>
                            <ContentDetailsGallery
                                findifyDataContext={findifyDataContext}
                                t={t}
                                content={content}
                                navigation={navigation}
                                onImagePress={(images: string[], index: number) => {
                                    setRoomGalleryImages(images);
                                    setRoomGalleryImageIndex(index);
                                }}
                            />
                        </View>
                    ) : null}
                </ScrollView>
            </View>
        </Modal>
        <Modal
            visible={roomGalleryImageIndex !== null}
            transparent={false}
            animationType="fade"
            onRequestClose={() => setRoomGalleryImageIndex(null)}>
            <View style={[
                styles.roomGalleryModalPage,
                { backgroundColor: colors.background.get() },
            ]}>
                <Pressable
                    style={[styles.roomGalleryModalClose, { backgroundColor: colors.primary.get() }]}
                    onPress={() => setRoomGalleryImageIndex(null)}>
                    <Icon name="close-outline" size={30} color="#fff" />
                </Pressable>
                {roomGalleryImageIndex !== null && roomGalleryImages[roomGalleryImageIndex] ? (
                    <Image
                        source={{ uri: roomGalleryImages[roomGalleryImageIndex] }}
                        resizeMode="contain"
                        style={styles.roomGalleryModalImage}
                    />
                ) : null}
                {roomGalleryImageIndex !== null && roomGalleryImageIndex > 0 ? (
                    <Pressable
                        style={styles.roomGalleryModalPrevious}
                        onPress={() => setRoomGalleryImageIndex((index) => Math.max(0, (index ?? 0) - 1))}>
                        <Icon name="chevron-back" size={34} color={colors.text.get()} />
                    </Pressable>
                ) : null}
                {roomGalleryImageIndex !== null && roomGalleryImageIndex < roomGalleryImages.length - 1 ? (
                    <Pressable
                        style={styles.roomGalleryModalNext}
                        onPress={() => setRoomGalleryImageIndex((index) => Math.min(roomGalleryImages.length - 1, (index ?? 0) + 1))}>
                        <Icon name="chevron-forward" size={34} color={colors.text.get()} />
                    </Pressable>
                ) : null}
                {roomGalleryImageIndex !== null ? (
                    <PlaceText style={[styles.roomGalleryModalCounter, { color: colors.text.get() }]}>
                        {roomGalleryImageIndex + 1} / {roomGalleryImages.length}
                    </PlaceText>
                ) : null}
            </View>
        </Modal>
        {bottomButtons}
    </>
}

const styles = StyleSheet.create({
    text: {
        padding: 10,
        paddingBottom: 10 + BottomNavigationPadding,
    },
    container: {
        height: '100%',
        width: '100%',
    },

    headerIcon: {
        padding: 5,
        ...IconShadow,
    },
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
    },
    errorTitle: {
        fontSize: 24,
        fontWeight: 'bold',
        marginBottom: 8,
    },
    errorText: {
        fontSize: 16,
        textAlign: 'center',
    },
    videoContainer: {
        width: '100%',
        height: 240,
        marginBottom: 18,
        borderRadius: 14,
        overflow: 'hidden',
        backgroundColor: '#000',
    },
    biscainhosPoiPage: {
        flex: 1,
        backgroundColor: '#f9f7f2',
    },
    biscainhosPoiHeader: {
        height: 46,
        backgroundColor: '#f9f7f2',
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(226, 177, 60, 0.45)',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
    },
    biscainhosPoiHeaderButton: {
        width: 40,
        height: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    biscainhosPoiLogo: {
        width: 118,
        height: 30,
    },
    biscainhosPoiHero: {
        aspectRatio: 4 / 5,
        width: '100%',
        backgroundColor: '#1f120f',
        position: 'relative',
        overflow: 'hidden',
    },
    biscainhosPoiHeroTablet: {
        maxWidth: 820,
        alignSelf: 'center',
        width: '100%',
    },
    biscainhosPoiHeroLandscapeTablet: {
        aspectRatio: 16 / 9,
        maxWidth: 1100,
    },
    biscainhosPoiHeroImage: {
        width: '100%',
        height: '100%',
        opacity: 0.92,
    },
    biscainhosPoiPlayButton: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        marginLeft: -40,
        marginTop: -40,
        width: 80,
        height: 80,
        borderRadius: 40,
        borderWidth: 2,
        borderColor: '#f5c443',
        backgroundColor: 'rgba(245, 196, 67, 0.86)',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
        shadowColor: '#000',
        shadowOpacity: 0.32,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 8 },
    },
    biscainhosHeroPlayHint: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        marginLeft: -40,
        marginTop: -40,
        width: 80,
        height: 80,
        borderRadius: 40,
        borderWidth: 2,
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
        shadowColor: '#000',
        shadowOpacity: 0.32,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 8 },
    },
    biscainhosPoiHeroCaption: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        paddingHorizontal: 24,
        paddingTop: 18,
        paddingBottom: 24,
        backgroundColor: 'rgba(153, 0, 0, 0.72)',
    },
    biscainhosPoiHighlightLabel: {
        color: '#f5c443',
        fontSize: 13,
        lineHeight: 18,
        fontWeight: '700',
        letterSpacing: 0.5,
    },
    biscainhosPoiHighlightTitle: {
        marginTop: 4,
        color: '#fff',
        fontFamily: 'Georgia',
        fontSize: 27,
        lineHeight: 32,
        fontWeight: '700',
    },
    biscainhosPoiContent: {
        paddingHorizontal: 24,
        paddingTop: 24,
        paddingBottom: BottomNavigationPadding + 118,
    },
    biscainhosPoiContentTablet: {
        width: '100%',
        maxWidth: 820,
        alignSelf: 'center',
        paddingHorizontal: 40,
        paddingTop: 34,
    },
    biscainhosPoiContentLandscapeTablet: {
        maxWidth: 980,
    },
    biscainhosPoiTitleCard: {
        backgroundColor: 'rgba(232, 229, 222, 0.72)',
        borderLeftWidth: 4,
        borderLeftColor: '#990000',
        paddingHorizontal: 16,
        paddingVertical: 14,
        marginBottom: 24,
        borderTopRightRadius: 8,
        borderBottomRightRadius: 8,
    },
    biscainhosPoiTitle: {
        color: '#990000',
        fontFamily: 'Georgia',
        fontSize: 29,
        lineHeight: 34,
        fontWeight: '700',
    },
    biscainhosPoiTitleRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 8,
    },
    biscainhosPoiTitleWithActions: {
        flex: 1,
    },
    poiQuickAction: {
        width: 42,
        height: 42,
        borderWidth: 1,
        borderRadius: 21,
        alignItems: 'center',
        justifyContent: 'center',
    },
    biscainhosPoiSubtitle: {
        marginTop: 4,
        color: '#3a2a28',
        fontSize: 17,
        lineHeight: 23,
        fontStyle: 'italic',
    },
    biscainhosPoiLanguageSection: {
        marginBottom: 28,
    },
    biscainhosPoiEnglishSection: {
        borderTopWidth: 1,
        borderTopColor: 'rgba(153, 0, 0, 0.22)',
        paddingTop: 20,
    },
    biscainhosPoiLanguageTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 10,
    },
    biscainhosPoiLanguageTitle: {
        color: '#990000',
        fontSize: 12,
        lineHeight: 16,
        letterSpacing: 3,
        fontWeight: '700',
    },
    biscainhosPoiParagraph: {
        color: '#241716',
        fontSize: 17,
        lineHeight: 27,
    },
    poiModelButton: {
        minHeight: 58,
        paddingHorizontal: 16,
        paddingVertical: 12,
        marginBottom: 28,
        borderWidth: 1,
        borderRadius: 10,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    poiModelButtonText: {
        flex: 1,
        fontSize: 16,
        fontWeight: '700',
    },
    poiArCard: {
        minHeight: 76,
        marginBottom: 28,
        padding: 14,
        borderWidth: 1,
        borderRadius: 10,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        shadowColor: '#000',
        shadowOpacity: 0.16,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 4 },
    },
    poiArIcon: {
        width: 46,
        height: 46,
        borderRadius: 23,
        alignItems: 'center',
        justifyContent: 'center',
    },
    poiArCopy: {
        flex: 1,
    },
    poiArTitle: {
        color: '#fff',
        fontFamily: 'Georgia',
        fontSize: 17,
        lineHeight: 22,
        fontWeight: '700',
    },
    poiArText: {
        marginTop: 3,
        color: 'rgba(255,255,255,0.84)',
        fontSize: 13,
        lineHeight: 18,
    },
    poiGallerySection: {
        marginBottom: 28,
    },
    roomInfoLinkCard: {
        minHeight: 64,
        borderWidth: 1,
        borderRadius: 8,
        paddingHorizontal: 14,
        paddingVertical: 10,
        marginBottom: 28,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    roomInfoLinkIcon: {
        width: 42,
        height: 42,
        borderRadius: 6,
        alignItems: 'center',
        justifyContent: 'center',
    },
    roomInfoLinkText: {
        flex: 1,
        fontFamily: 'Georgia',
        fontSize: 19,
        lineHeight: 24,
        fontWeight: '700',
    },
    featuredCollectionSection: {
        marginTop: 4,
        marginBottom: 28,
    },
    featuredCollectionTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 18,
    },
    featuredCollectionLine: {
        flex: 1,
        height: 1,
    },
    featuredCollectionTitle: {
        fontFamily: 'Georgia',
        fontSize: 23,
        lineHeight: 29,
        fontWeight: '700',
        textAlign: 'center',
    },
    featuredObjectRow: {
        minHeight: 84,
        marginBottom: 14,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
    },
    featuredObjectThumbWrap: {
        width: 84,
        height: 84,
        backgroundColor: 'rgba(255,255,255,0.45)',
        overflow: 'hidden',
    },
    featuredObjectThumb: {
        width: '100%',
        height: '100%',
    },
    featuredObjectTitle: {
        flex: 1,
        fontSize: 18,
        lineHeight: 23,
        fontWeight: '600',
    },
    roomInfoModalPage: {
        flex: 1,
    },
    floorMapModalBackdrop: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.62)',
        justifyContent: 'center',
        padding: 20,
    },
    floorMapModal: {
        maxHeight: '86%',
        borderRadius: 12,
        padding: 20,
        shadowColor: '#000',
        shadowOpacity: 0.28,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 8 },
    },
    floorMapClose: {
        position: 'absolute',
        zIndex: 2,
        top: 12,
        right: 12,
        width: 40,
        height: 40,
        borderRadius: 20,
        alignItems: 'center',
        justifyContent: 'center',
    },
    floorMapTitle: {
        paddingRight: 44,
        fontFamily: 'Georgia',
        fontSize: 25,
        lineHeight: 31,
        fontWeight: '700',
    },
    floorMapSubtitle: {
        marginTop: 4,
        marginBottom: 16,
        fontSize: 14,
        lineHeight: 19,
    },
    floorMapImage: {
        width: '100%',
        aspectRatio: 1,
        position: 'relative',
        backgroundColor: 'rgba(0,0,0,0.06)',
    },
    floorMapImageFill: {
        width: '100%',
        height: '100%',
    },
    floorMapMarker: {
        position: 'absolute',
    },
    floorMapEmpty: {
        minHeight: 180,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        backgroundColor: 'rgba(0,0,0,0.05)',
    },
    floorMapLegend: {
        marginTop: 14,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
    },
    floorMapLegendText: {
        fontSize: 13,
        marginRight: 8,
    },
    roomInfoModalClose: {
        position: 'absolute',
        top: 64,
        right: 18,
        zIndex: 5,
        width: 44,
        height: 44,
        borderRadius: 22,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#000',
        shadowOpacity: 0.24,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 3 },
    },
    roomInfoModalContent: {
        paddingHorizontal: 24,
        paddingTop: 34,
        paddingBottom: BottomNavigationPadding + 56,
    },
    roomInfoModalContentTablet: {
        width: '100%',
        maxWidth: 820,
        alignSelf: 'center',
        paddingHorizontal: 40,
    },
    roomInfoModalTitle: {
        fontFamily: 'Georgia',
        fontSize: 34,
        lineHeight: 40,
        fontWeight: '700',
        marginBottom: 24,
        paddingRight: 48,
    },
    roomInfoModalFeaturedImage: {
        width: '100%',
        aspectRatio: 16 / 9,
        borderRadius: 10,
        marginBottom: 24,
        backgroundColor: 'rgba(0,0,0,0.08)',
    },
    roomInfoModalModelButton: {
        marginTop: 20,
        padding: 14,
        borderWidth: 1,
        borderRadius: 10,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
    },
    roomInfoModalModelButtonText: {
        fontSize: 16,
        fontWeight: '700',
    },
    roomInfoModalGallery: {
        marginTop: 4,
    },
    roomGalleryModalPage: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
    },
    roomGalleryModalImage: {
        width: '100%',
        height: '100%',
    },
    roomGalleryModalClose: {
        position: 'absolute',
        top: 18,
        right: 18,
        zIndex: 2,
        width: 44,
        height: 44,
        borderRadius: 22,
        alignItems: 'center',
        justifyContent: 'center',
    },
    roomGalleryModalPrevious: {
        position: 'absolute',
        left: 12,
        top: '50%' as any,
        marginTop: -24,
        width: 48,
        height: 48,
        borderRadius: 24,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255,255,255,0.78)',
    },
    roomGalleryModalNext: {
        position: 'absolute',
        right: 12,
        top: '50%' as any,
        marginTop: -24,
        width: 48,
        height: 48,
        borderRadius: 24,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255,255,255,0.78)',
    },
    roomGalleryModalCounter: {
        position: 'absolute',
        top: 26,
        left: 24,
        fontSize: 16,
        fontWeight: '700',
    },
    biscainhosPoiNextCard: {
        marginTop: 2,
        borderRadius: 8,
        borderWidth: 1,
        backgroundColor: '#b00000',
        padding: 20,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        shadowColor: '#000',
        shadowOpacity: 0.18,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 4 },
    },
    biscainhosPoiNavigation: {
        gap: 14,
    },
    biscainhosPoiPreviousCard: {
        shadowOpacity: 0.08,
    },
    biscainhosPoiNextTitle: {
        color: '#fff',
        fontFamily: 'Georgia',
        fontSize: 25,
        lineHeight: 30,
        fontWeight: '700',
    },
    biscainhosPoiNextCopy: {
        marginTop: 5,
        color: 'rgba(255,255,255,0.85)',
        fontSize: 14,
        lineHeight: 19,
    },
    biscainhosPoiNextCopyStrong: {
        color: '#f5c443',
        fontWeight: '700',
    },
    biscainhosPoiNextIcon: {
        width: 54,
        height: 54,
        borderRadius: 27,
        backgroundColor: '#f5c443',
        alignItems: 'center',
        justifyContent: 'center',
    },
    biscainhosDetailsGallery: {
        marginTop: 6,
    },
    biscainhosVideoModal: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.92)',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
    },
    biscainhosVideoClose: {
        position: 'absolute',
        top: 18,
        right: 18,
        zIndex: 10,
        width: 46,
        height: 46,
        borderRadius: 23,
        backgroundColor: 'rgba(153, 0, 0, 0.9)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    reviewPage: {
        flex: 1,
        backgroundColor: '#f9f7f2',
    },
    reviewHeader: {
        height: 56,
        backgroundColor: '#f9f7f2',
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(226, 177, 60, 0.45)',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 28,
    },
    reviewHeaderButton: {
        width: 40,
        height: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    reviewLogo: {
        width: 126,
        height: 32,
    },
    reviewContent: {
        paddingHorizontal: 20,
        paddingTop: 48,
        paddingBottom: BottomNavigationPadding + 118,
    },
    reviewContentTablet: {
        width: '100%',
        maxWidth: 760,
        alignSelf: 'center',
        paddingHorizontal: 40,
    },
    reviewContentLandscapeTablet: {
        maxWidth: 980,
    },
    reviewTitleBlock: {
        alignItems: 'center',
        marginBottom: 46,
    },
    reviewTitle: {
        color: '#990000',
        fontFamily: 'Georgia',
        fontSize: 52,
        lineHeight: 58,
        fontWeight: '700',
        textAlign: 'center',
    },
    reviewLead: {
        marginTop: 22,
        color: '#3a1f1d',
        fontSize: 19,
        lineHeight: 26,
        textAlign: 'center',
    },
    reviewLeadItalic: {
        marginTop: 5,
        color: 'rgba(58, 31, 29, 0.74)',
        fontSize: 16,
        lineHeight: 22,
        fontStyle: 'italic',
        textAlign: 'center',
    },
    reviewQuestionCard: {
        minHeight: 204,
        borderWidth: 1,
        borderColor: 'rgba(153, 0, 0, 0.28)',
        borderRadius: 4,
        backgroundColor: 'rgba(255,255,255,0.58)',
        paddingHorizontal: 24,
        paddingVertical: 24,
        marginBottom: 32,
    },
    reviewQuestionTitle: {
        color: '#0f0908',
        fontFamily: 'Georgia',
        fontSize: 28,
        lineHeight: 34,
        fontWeight: '700',
    },
    reviewQuestionSubtitle: {
        marginTop: 6,
        color: '#3a1f1d',
        fontSize: 13,
        lineHeight: 18,
        letterSpacing: 2,
        textTransform: 'uppercase',
    },
    reviewRatingRow: {
        marginTop: 42,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    reviewTextInput: {
        minHeight: 150,
        marginTop: 26,
        borderWidth: 1,
        borderColor: 'rgba(153, 0, 0, 0.32)',
        borderRadius: 6,
        backgroundColor: '#fff',
        color: '#0f0908',
        paddingHorizontal: 14,
        paddingVertical: 12,
        fontSize: 16,
        lineHeight: 23,
    },
    reviewRatingButton: {
        width: 42,
        height: 42,
        alignItems: 'center',
        justifyContent: 'center',
    },
    reviewSubmitButton: {
        height: 72,
        borderRadius: 3,
        backgroundColor: '#b00000',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 34,
        shadowColor: '#000',
        shadowOpacity: 0.18,
        shadowRadius: 7,
        shadowOffset: { width: 0, height: 4 },
    },
    reviewSubmitText: {
        color: '#fff',
        fontFamily: 'Georgia',
        fontSize: 21,
        lineHeight: 27,
        fontWeight: '700',
        textAlign: 'center',
    },
    reviewSubmitSubtext: {
        color: '#fff',
        fontSize: 13,
        lineHeight: 18,
        fontStyle: 'italic',
        textAlign: 'center',
    },
    reviewBottomImage: {
        width: '100%',
        height: 190,
        borderRadius: 3,
        marginTop: 82,
    },
    routeMapSection: {
        marginTop: 22,
        gap: 12,
    },
    routeMapHeader: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
    },
    routeMapTitle: {
        fontSize: 22,
        lineHeight: 28,
        fontWeight: 'bold',
    },
    routeMapCount: {
        fontSize: 14,
        lineHeight: 20,
    },
    routeMapContainer: {
        height: 320,
        width: '100%',
        borderRadius: 16,
        overflow: 'hidden',
        backgroundColor: '#d7f5fb',
    },
    routeMap: {
        flex: 1,
    },
    routeMapOverlay: {
        position: 'absolute',
        left: 12,
        right: 12,
        bottom: 12,
        minHeight: 72,
        borderRadius: 12,
        padding: 12,
        paddingRight: 34,
        backgroundColor: 'rgba(255,255,255,0.96)',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        zIndex: 1000,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.2,
        shadowRadius: 8,
    },
    routeMapOverlayClose: {
        position: 'absolute',
        right: -8,
        top: -8,
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: '#000',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
    },
    routeMapOverlayNumber: {
        width: 34,
        height: 34,
        borderRadius: 17,
        backgroundColor: '#000',
        borderWidth: 2,
        borderColor: '#ffc42d',
        alignItems: 'center',
        justifyContent: 'center',
    },
    routeMapOverlayNumberText: {
        color: '#fff',
        fontSize: 15,
        lineHeight: 18,
        fontWeight: 'bold',
    },
    routeMapOverlayTitle: {
        fontSize: 15,
        lineHeight: 20,
        fontWeight: '600',
    },
    routeMapOverlaySubtitle: {
        fontSize: 13,
        lineHeight: 18,
        marginTop: 2,
    },
    routeMarker: {
        width: 30,
        height: 30,
        borderRadius: 15,
        backgroundColor: '#000',
        borderWidth: 2,
        borderColor: '#ffc42d',
        alignItems: 'center',
        justifyContent: 'center',
    },
    routeMarkerText: {
        color: '#fff',
        fontSize: 14,
        lineHeight: 18,
        fontWeight: 'bold',
    },
    routePointsList: {
        gap: 8,
    },
    routePointLine: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 8,
    },
    routePointNumber: {
        width: 26,
        height: 26,
        borderRadius: 13,
        backgroundColor: '#000',
        borderWidth: 2,
        borderColor: '#ffc42d',
        alignItems: 'center',
        justifyContent: 'center',
    },
    routePointNumberText: {
        color: '#fff',
        fontSize: 13,
        lineHeight: 16,
        fontWeight: 'bold',
    },
    routePointTitle: {
        flex: 1,
        fontSize: 15,
        lineHeight: 20,
    }
})

const getCoordsFromPoint = (pointGeometry: string) => {
    const coordinatesMatch = pointGeometry?.match(/\(([^)]+)\)/);
    if (!coordinatesMatch) return null;

    const coordinatesArray = coordinatesMatch[1].split(' ');
    const latitude = parseFloat(coordinatesArray[0]);
    const longitude = parseFloat(coordinatesArray[1]);

    if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;
    return { latitude, longitude };
}

const getRoutePoints = (content: any) => {
    return [...(content?.points_of_interest ?? [])]
        .sort((a: any, b: any) => Number(a.sequence ?? 0) - Number(b.sequence ?? 0))
        .map((line: any, index: number) => {
            const poi = line.point_of_interest ?? line;
            const coords = getCoordsFromPoint(poi?.lat_long);
            if (!coords) return null;

            return {
                ...line,
                point: poi,
                index,
                latitude: coords.latitude,
                longitude: coords.longitude,
            }
        })
        .filter(Boolean);
}

const makeRouteLineShape = (points: any[]) => ({
    type: 'Feature',
    geometry: {
        type: 'LineString',
        coordinates: points.map((point: any) => [point.longitude, point.latitude]),
    },
    properties: {},
});

const makeRouteBounds = (points: any[]) => {
    const latitudes = points.map((point: any) => point.latitude);
    const longitudes = points.map((point: any) => point.longitude);

    return {
        sw: [Math.min(...longitudes), Math.min(...latitudes)],
        ne: [Math.max(...longitudes), Math.max(...latitudes)],
        paddingLeft: 0.00001,
        paddingRight: 0.00001,
        paddingTop: 0.00001,
        paddingBottom: 0.00001,
    };
}

const RouteDetailsMap = ({ content, findifyDataContext, navigation }: any) => {
    const points = useMemo(() => getRoutePoints(content), [content]);
    const [routeShape, setRouteShape] = useState<any>(null);
    const [selectedRoutePoint, setSelectedRoutePoint] = useState<any>(null);

    const fallbackLineShape = useMemo(() => makeRouteLineShape(points), [points]);

    useEffect(() => {
        if (points.length < 2 || content?.geojson) return;

        const coordinates = points.map((point: any) => `${point.longitude},${point.latitude}`).join(';');
        const url = `https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=full&geometries=geojson`;

        fetch(url)
            .then((response) => response.ok ? response.json() : null)
            .then((data) => {
                const geometry = data?.routes?.[0]?.geometry;
                if (!geometry?.coordinates?.length) return;

                setRouteShape({
                    type: 'Feature',
                    geometry,
                    properties: {},
                });
            })
            .catch(() => {
                setRouteShape(null);
            });
    }, [content?.geojson, points]);

    if (points.length === 0) return null;

    const lineShape = content?.geojson
        ? (typeof content.geojson === 'string' ? JSON.parse(content.geojson) : content.geojson)
        : (routeShape ?? fallbackLineShape);
    const bounds = makeRouteBounds(points);
    const centerCoordinate = [
        (bounds.sw[0] + bounds.ne[0]) / 2,
        (bounds.sw[1] + bounds.ne[1]) / 2,
    ];

    return <View style={styles.routeMapSection}>
        <View style={styles.routeMapHeader}>
            <PlaceText style={[styles.routeMapTitle, { color: PlaceColors(findifyDataContext).primary.get() }]}>
                Rota
            </PlaceText>
            <PlaceText style={[styles.routeMapCount, { color: PlaceColors(findifyDataContext).text.withAlpha(180) }]}>
                {points.length} pontos
            </PlaceText>
        </View>
        <View style={styles.routeMapContainer}>
            <RNMapbox.MapView style={styles.routeMap} styleURL={StyleURL.Light}>
                <RNMapbox.Camera
                    bounds={bounds}
                    centerCoordinate={centerCoordinate}
                    zoomLevel={content?.map_zoom ? Number(content.map_zoom) : 15}
                />
                <RNMapbox.ShapeSource id="routeLine" shape={lineShape}>
                    <RNMapbox.LineLayer id="routeLineLayer" sourceID="routeLine" />
                </RNMapbox.ShapeSource>
                {points.map((routePoint: any) => {
                    const title = getTranslatedString(routePoint.point?.title, findifyDataContext);
                    return <RNMapbox.MarkerView
                        key={routePoint.route_point_of_interest_id ?? routePoint.point_of_interest_id}
                        coordinate={[routePoint.longitude, routePoint.latitude]}
                        anchor={{ x: 0.5, y: 0.5 }}
                    >
                        <Pressable
                            onPress={() => {
                                setSelectedRoutePoint(routePoint);
                            }}
                            style={styles.routeMarker}
                        >
                            <PlaceText style={styles.routeMarkerText}>{routePoint.index + 1}</PlaceText>
                        </Pressable>
                    </RNMapbox.MarkerView>
                })}
            </RNMapbox.MapView>
            {!!selectedRoutePoint && <Pressable
                style={styles.routeMapOverlay}
                onPress={() => {
                    navigation.push('PlaceContentDetails', {
                        module: 'points_of_interest',
                        content_id: selectedRoutePoint.point?.point_of_interest_id,
                        page_slug: selectedRoutePoint.point?.page_slug,
                    });
                }}
            >
                <Pressable
                    style={styles.routeMapOverlayClose}
                    onPress={() => setSelectedRoutePoint(null)}
                >
                    <Icon name="close" size={18} color="#fff" />
                </Pressable>
                <View style={styles.routeMapOverlayNumber}>
                    <PlaceText style={styles.routeMapOverlayNumberText}>{selectedRoutePoint.index + 1}</PlaceText>
                </View>
                <View style={{ flex: 1 }}>
                    <PlaceText style={[styles.routeMapOverlayTitle, { color: PlaceColors(findifyDataContext).text.get() }]}>
                        {getTranslatedString(selectedRoutePoint.point?.title, findifyDataContext)}
                    </PlaceText>
                    {!!selectedRoutePoint.point?.address && <PlaceText style={[styles.routeMapOverlaySubtitle, { color: PlaceColors(findifyDataContext).text.withAlpha(170) }]}>
                        {selectedRoutePoint.point.address}
                    </PlaceText>}
                </View>
                <Icon name="chevron-forward" size={20} color={PlaceColors(findifyDataContext).text.withAlpha(140)} />
            </Pressable>}
        </View>
        <View style={styles.routePointsList}>
            {points.map((routePoint: any) => {
                const title = getTranslatedString(routePoint.point?.title, findifyDataContext);
                return <Pressable
                    key={`route_point_${routePoint.route_point_of_interest_id ?? routePoint.point_of_interest_id}`}
                    style={styles.routePointLine}
                    onPress={() => {
                        navigation.push('PlaceContentDetails', {
                            module: 'points_of_interest',
                            content_id: routePoint.point?.point_of_interest_id,
                            page_slug: routePoint.point?.page_slug,
                        });
                    }}
                >
                    <View style={styles.routePointNumber}>
                        <PlaceText style={styles.routePointNumberText}>{routePoint.index + 1}</PlaceText>
                    </View>
                    <PlaceText style={[styles.routePointTitle, { color: PlaceColors(findifyDataContext).text.get() }]}>
                        {title}
                    </PlaceText>
                    <Icon name="chevron-forward" size={18} color={PlaceColors(findifyDataContext).text.withAlpha(120)} />
                </Pressable>
            })}
        </View>
    </View>
}
